import React, { useEffect, useMemo, useState } from 'react';
import {
  syncGoogleAdsLeadSources,
  getGoogleAdsLeadSources,
  getProjectsByCompanyId,
  getAllSalesUsersByProjectId,
  updateGoogleAdsLeadSource
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import NoDataFound from '../components/layout/NoDataFound';
import Modal from '../../shared/components/ui/Modal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Multiselect from 'multiselect-react-dropdown';

const SOURCE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'lead_form', label: 'Lead Forms' },
  { id: 'website_campaign', label: 'Website Campaigns' }
];

const GoogleAdsLeadSources = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const location = useLocation();
  const cmpId = location.state?.cmpId;
  const companyName = location.state?.companyName;
  const { collapsed } = useSidebar();

  const [activeTab, setActiveTab] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [sourceId, setSourceId] = useState(null);
  const [updateData, setUpdateData] = useState({ project_id: '', sales_ids: [] });
  const [selectedSalesPersons, setSelectedSalesPersons] = useState([]);

  useEffect(() => {
    mixpanel.track('Google Ads Lead Sources Viewed', {
      user_id: userId,
      company_id: cmpId,
      timestamp: new Date().toISOString()
    });
  }, [userId, cmpId]);

  const {
    data: leadSources,
    refetch: refetchLeadSources,
    isPending: isLeadSourcesPending
  } = useQuery({
    queryKey: ['googleAdsLeadSources', cmpId, activeTab],
    queryFn: () =>
      getGoogleAdsLeadSources(
        cmpId,
        activeTab === 'all' ? undefined : activeTab
      ),
    enabled: !!cmpId
  });

  const { data: projects } = useQuery({
    queryKey: ['project_byId', cmpId],
    queryFn: () => getProjectsByCompanyId(cmpId),
    enabled: !!cmpId
  });

  const { data: sales } = useQuery({
    queryKey: ['salesUsers', updateData.project_id],
    queryFn: () => getAllSalesUsersByProjectId(updateData.project_id, 1, 500),
    enabled: !!updateData.project_id
  });

  const filteredSources = useMemo(() => {
    if (!Array.isArray(leadSources)) {
      return [];
    }
    if (activeTab === 'all') {
      return leadSources;
    }
    return leadSources.filter((s) => s.source_type === activeTab);
  }, [leadSources, activeTab]);

  const { mutate: syncSources, isPending: isSyncing } = useMutation({
    mutationFn: () => syncGoogleAdsLeadSources(cmpId),
    onSuccess: async () => {
      toast.success('Lead sources synced from Google Ads');
      await refetchLeadSources();
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to sync lead sources'
      );
    }
  });

  const { mutate: saveMapping, isPending: isSaving } = useMutation({
    mutationFn: ({ id, body }) => updateGoogleAdsLeadSource(id, body),
    onSuccess: async () => {
      toast.success('Mapping saved — prelead list created');
      setOpenModal(false);
      setSourceId(null);
      setUpdateData({ project_id: '', sales_ids: [] });
      setSelectedSalesPersons([]);
      await refetchLeadSources();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save mapping');
    }
  });

  const handleSync = () => {
    if (!cmpId) {
      toast.error('Company id not found');
      return;
    }
    syncSources();
  };

  const handleShowModal = (source) => {
    const projectId = source.project_id ? String(source.project_id) : '';
    setUpdateData({
      project_id: projectId,
      sales_ids: source.sales_persons?.map((p) => p.id) || []
    });
    setSelectedSalesPersons(source.sales_persons || []);
    setSourceId(source.id);
    setOpenModal(true);
  };

  const handleSubmitMapping = () => {
    if (!updateData.project_id || !updateData.sales_ids?.length) {
      toast.error('Please select project and sales persons');
      return;
    }
    if (!sourceId) {
      toast.error('Lead source not found');
      return;
    }
    saveMapping({
      id: sourceId,
      body: {
        project_id: parseInt(updateData.project_id, 10),
        sales_ids: updateData.sales_ids
      }
    });
  };

  const sourceTypeLabel = (type) => {
    if (type === 'lead_form') return 'Lead Form';
    if (type === 'website_campaign') return 'Website Campaign';
    return type;
  };

  if (!cmpId) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <p className="mb-4">No company selected.</p>
          <Button onClick={() => navigate('/builder/integration/google-ads-companies')}>
            Back to Google Ads
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={true}
          onBack={() => navigate('/builder/integration/google-ads-companies')}
          data={{
            name: `Integrations > Google Ads${companyName ? ` > ${companyName}` : ''} > Lead Sources`
          }}
        />
      </div>

      <div
        className={`flex z-20 items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <div className="flex gap-2">
          {SOURCE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 rounded text-sm ${
                activeTab === tab.id
                  ? 'bg-[#2A565E] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
        <Button onClick={handleSync} isLoading={isSyncing} disabled={isSyncing}>
          Sync from Google Ads
        </Button>
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[9rem] w-full h-full bg-gray-100 p-6">
          {isLeadSourcesPending ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#346C75] border-t-transparent" />
            </div>
          ) : filteredSources.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSources.map((source) => (
                <div key={source.id} className="bg-white shadow-md p-6 rounded-lg flex flex-col">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-medium text-[#2A565E] bg-[#E8F4F3] px-2 py-0.5 rounded">
                      {sourceTypeLabel(source.source_type)}
                    </span>
                    {source.project_id ? (
                      <span className="text-xs text-[#418b7a] font-medium">Mapped</span>
                    ) : (
                      <span className="text-xs text-amber-600 font-medium">Unmapped</span>
                    )}
                  </div>
                  <h3 className="font-bold text-lg mb-2">{source.name}</h3>
                  {source.status && (
                    <p className="text-xs text-gray-500 mb-2">Status: {source.status}</p>
                  )}
                  {source.project?.name && (
                    <p className="text-sm text-gray-600 mb-4">Project: {source.project.name}</p>
                  )}
                  {source.pre_lead_list?.name && (
                    <p className="text-sm text-gray-600 mb-4">
                      Prelead list: {source.pre_lead_list.name}
                    </p>
                  )}
                  <div className="mt-auto">
                    <Button onClick={() => handleShowModal(source)}>
                      {source.project_id ? 'Update Mapping' : 'Map to Project'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <NoDataFound disabledBtn={true} screenHeight={false} />
          )}
        </div>
      </div>

      {openModal && (
        <Modal
          isDirty={true}
          width={60}
          name="Map Lead Source"
          onClose={() => {
            setOpenModal(false);
            setSourceId(null);
            setUpdateData({ project_id: '', sales_ids: [] });
            setSelectedSalesPersons([]);
          }}>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <p>Project</p>
              <select
                className="w-full p-2 border-[1px] border-stone-300 rounded"
                value={updateData.project_id}
                onChange={(e) => {
                  setUpdateData((prev) => ({
                    ...prev,
                    project_id: e.target.value,
                    sales_ids: []
                  }));
                  setSelectedSalesPersons([]);
                }}>
                <option value="" disabled>
                  Select a project
                </option>
                {projects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <p>Sales</p>
              <Multiselect
                options={sales?.data || []}
                selectedValues={updateData.project_id ? selectedSalesPersons : []}
                onSelect={(selectedList) => {
                  setSelectedSalesPersons(selectedList);
                  setUpdateData((prev) => ({
                    ...prev,
                    sales_ids: selectedList.map((person) => person.id)
                  }));
                }}
                onRemove={(selectedList) => {
                  setSelectedSalesPersons(selectedList);
                  setUpdateData((prev) => ({
                    ...prev,
                    sales_ids: selectedList.map((person) => person.id)
                  }));
                }}
                displayValue="name"
                placeholder="Select sales persons"
                style={{
                  chips: { background: '#2A565E', color: 'white' },
                  searchBox: {
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    background: 'white',
                    color: '#333'
                  }
                }}
              />
            </div>
          </div>

          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => {
                setOpenModal(false);
                setSourceId(null);
              }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitMapping} isLoading={isSaving} disabled={isSaving}>
              Submit
            </Button>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default GoogleAdsLeadSources;
